import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {MigrationObjectPlan} from 'config-migration/MigrationConstants';
import {OBJECT_TYPE_TO_DISPLAY_TEXT, ObjectType} from 'config-migration/types/ObjectType';
import getTestProps from 'test/lib/cypress/getTestProps';

import {ObjectActionType} from '../../types/objectActions';
import {ImportPlanSectionProps, ImportPlanSummary} from '../import-plan-sections/ImportPlanSection';
import ObjectActionDropdown from '../ObjectActionDropdown';
import TemplatePlanRow from './TemplatePlanRow';

export function TemplatePlanHeader(): JSX.Element {
  return (
    <>
      <p>
        Imported entry templates and sub-templates may contain placeholders where there was a reference to
        data that is not imported by this tool.
      </p>
      <p>
        A Template Collection may be included on this page because an Execution template belongs in it.
        Execution templates will be imported on the next page with Workflow items.
      </p>
      <p>
        Pre-filled schema field values will not be migrated. Newly created entry templates will have empty
        schema field values. Existing schema field values in the destination will be unchanged.
      </p>
    </>
  );
}

export default function TemplatePlan(props: ImportPlanSectionProps): JSX.Element {
  const templateCollectionPlan = props.plans[0];
  const templatePlans = props.plans.slice(1);

  const onUpdateTemplateCollectionPlan = (templateCollectionPlan: MigrationObjectPlan): void => {
    if (templateCollectionPlan.selectedAction.type === ObjectActionType.IGNORE) {
      const updatedTemplatePlans: Array<MigrationObjectPlan> = templatePlans.map((plan) => ({
        ...plan,
        selectedAction: {type: ObjectActionType.IGNORE},
      }));
      props.onUpdatePlans([templateCollectionPlan, ...updatedTemplatePlans]);
    } else {
      props.onUpdatePlans([
        templateCollectionPlan,
        ...templatePlans.map((plan) => ({...plan, selectedAction: plan.initialAction})),
      ]);
      props.onUpdateCollapsed(false);
    }
  };

  return (
    <div className="simpleDivTable u-spacing-top">
      <div className={classNames('simpleDivTable-header', 'u-lr-split', css(styles.summaryRow))}>
        <h4>{OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.TemplateCollection]}</h4>
        <ImportPlanSummary
          plans={props.plans}
          onUpdateCollapsed={props.onUpdateCollapsed}
          collapsed={props.collapsed}
        />
      </div>
      <div
        className="simpleDivTable-header"
        {...getTestProps({component: 'TemplateSection', element: 'tableHeader'})}
      >
        <div className={classNames('simpleDivTable-headerCell u-black-color', css(styles.firstCell))}>
          <strong>{templateCollectionPlan.objectDisplayName}</strong>
        </div>
        <div className="simpleDivTable-headerCell">
          <ObjectActionDropdown
            object={templateCollectionPlan}
            onUpdateObject={onUpdateTemplateCollectionPlan}
          />
        </div>
      </div>
      {!props.collapsed && (
        <>
          {templatePlans.map((plan, index) => (
            <TemplatePlanRow
              key={plan.objectId}
              plan={plan}
              errors={props.errorsByObjectId[plan.objectId] || []}
              onUpdatePlan={(updatedPlan) => {
                props.onUpdatePlans([updatedPlan]);
              }}
              templateCollectionPlan={templateCollectionPlan}
            />
          ))}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  summaryRow: {
    paddingLeft: 24,
    paddingRight: 24,
    marginBottom: 2,
  },
  firstCell: {
    marginLeft: 12,
  },
});
